/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import type { AnyAction, SerializedError } from '@reduxjs/toolkit';

import { isActionsSynonymRecommendationsEnabled } from '../../../ia/store/features/selectors';
import * as actionType from './actionTypes';

export const listExistingValueRecommendations =
  (
    workspaceId: string,
    entityId: string,
    valueId: string,
    negativeExamples: string[],
    includeDetails: boolean,
    skillId: string
  ) =>
  async (dispatch, getState) => {
    const state = getState();
    const invokeApi = isActionsSynonymRecommendationsEnabled(state);
    dispatch({
      type: actionType.FETCH_RECOMMENDATIONS_EXISTING_VALUE,
      workspaceId,
      entityId,
      valueId,
      negativeExamples,
      includeDetails,
      skillId,
      invokeApi
    });
  };

export const addExistingValueRecommendations = (
  newRecommendations: string[]
): AnyAction => ({
  type: actionType.ADD_RECOMMENDATIONS_EXISTING_VALUE,
  newRecommendations
});

export const listExistingValueRecommendationsError = (
  error: SerializedError
): AnyAction => ({
  type: actionType.FETCH_RECOMMENDATIONS_ERROR_EXISTING_VALUE,
  error
});

export const listNewValueRecommendations =
  (
    workspaceId: string,
    entityId: string,
    valueId: string,
    synonyms: string[],
    negativeExamples: string[],
    includeDetails: boolean,
    skillId: string
  ) =>
  async (dispatch, getState) => {
    const state = getState();
    const invokeApi = isActionsSynonymRecommendationsEnabled(state);

    return dispatch({
      type: actionType.FETCH_RECOMMENDATIONS_NEW_VALUE,
      workspaceId,
      entityId,
      valueId,
      synonyms,
      negativeExamples,
      includeDetails,
      invokeApi,
      skillId
    });
  };

export const addNewValueRecommendations = (
  newRecommendations: string[]
): AnyAction => ({
  type: actionType.ADD_RECOMMENDATIONS_NEW_VALUE,
  newRecommendations
});

export const listNewValueRecommendationsError = (
  error: SerializedError
): AnyAction => ({
  type: actionType.FETCH_RECOMMENDATIONS_ERROR_NEW_VALUE,
  error
});

// Recommendation Sources
export const listRecommendationSourcesStart = (
  workspaceId: string,
  skillId: string,
  showingNewIA: boolean,
  initialCall?: boolean
): AnyAction => ({
  type: actionType.FETCH_RECOMMENDATION_SOURCES_START,
  workspaceId,
  skillId,
  showingNewIA,
  initialCall
});

export const listRecommendationSourcesSuccess = (
  sources: string[],
  migrated: boolean
): AnyAction => ({
  type: actionType.FETCH_RECOMMENDATION_SOURCES_SUCCESS,
  sources,
  migrated
});

export const listRecommendationSourcesError = (
  error: SerializedError
): AnyAction => ({
  type: actionType.FETCH_RECOMMENDATION_SOURCES_ERROR,
  error
});

export const setRecommendationSourcesStart = (
  workspaceId: string,
  skillId: string,
  sources: string[],
  showingNewIA: boolean
): AnyAction => ({
  type: actionType.SET_RECOMMENDATION_SOURCES_START,
  workspaceId,
  skillId,
  sources,
  showingNewIA
});

export const setRecommendationSourcesSuccess = (
  sources: string[],
  status: number
): AnyAction => ({
  type: actionType.SET_RECOMMENDATION_SOURCES_SUCCESS,
  sources,
  status
});

export const setRecommendationSourcesError = (
  error: SerializedError
): AnyAction => ({
  type: actionType.SET_RECOMMENDATION_SOURCES_ERROR,
  error
});

export const toggleRecommendationSources = (
  fromComponent?: string
): AnyAction => ({
  type: actionType.TOGGLE_RECOMMENDATION_SOURCES,
  fromComponent
});

export const toggleRecSourceAssistants = (
  fromComponent: string
): AnyAction => ({
  type: actionType.TOGGLE_REC_SOURCE_ASSISTANTS,
  fromComponent
});

export const toggleFileUploader = (fromComponent: string): AnyAction => ({
  type: actionType.TOGGLE_FILE_UPLOADER,
  fromComponent
});
